import Client from '../Client';
import Storage from './storage';
import LocaleStorage from './storage/LocaleStorage';
import {
  BASE_API_URL,
  ERR_INTERNET_DISCONNECTED_MESSAGE,
  LS_I18NEXT_LNG_KEY,
  ORIGINS,
  TOKEN_STORAGE_KEY,
} from './constants';

const FAILURE_RESPONSE_STATUSES = [400, 401, 429];

function getURL(endpoint = '', version: string | number = 3) {
  if (version === 0) {
    return `${BASE_API_URL}/api/${endpoint}`;
  }

  const handledVersion = typeof version === 'number' ? `v${version}` : version;

  return `${BASE_API_URL}/api/${handledVersion}/${endpoint}`;
}

function getChekinURLToken(token: string) {
  return `${BASE_API_URL}/L/v4/${token}`;
}
type AuthTypes = {
  [key: string]: string;
};

function getAuthAndBaseHeaders(): AuthTypes {
  const token = getTokenFromLocalStorage();
  const language = LocaleStorage.get<string>(LS_I18NEXT_LNG_KEY);
  const languageCode = language?.slice(0, 2);
  const isChekinConsumer = Client.initParams?.isChekinConsumer;

  return {
    Authorization: `JWT ${token}`,
    'Content-Type': 'application/json',
    ...(languageCode ? {'Accept-Language': languageCode} : undefined),
    'X-source': isChekinConsumer ? ORIGINS.guestAppV2 : ORIGINS.embeddedGuestApp,
  };
}

function getAnonymousHeaders() {
  const storedApiKey = Client.initParams?.apikey;
  const isChekinConsumer = Client.initParams?.isChekinConsumer;
  const initHeader: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-source': isChekinConsumer ? ORIGINS.guestAppV2 : ORIGINS.embeddedGuestApp,
  };

  if (!isChekinConsumer) {
    initHeader['embedded-sdk-app'] = storedApiKey;
  }

  return initHeader;
}

function getTokenFromLocalStorage(): string | null {
  let token = null;

  try {
    const storageToken = Storage.get<string>(TOKEN_STORAGE_KEY);
    if (storageToken !== null) {
      token = storageToken;
    }
  } catch (err) {
    console.error(err);
  }
  return token;
}

function persistTokenToLocalStorage(token: null | string = ''): void {
  Storage.set(TOKEN_STORAGE_KEY, token);
}

function removeTokenFromLocalStorage(): void {
  Storage.remove(TOKEN_STORAGE_KEY);
}

function getAndHandleResponseError<T>(response?: Response, body?: T, message?: string) {
  const status = response?.status;

  if (message === 'Failed to fetch') {
    return {
      message: `You have not internet connection.`,
      status: 1001,
      body,
      name: ERR_INTERNET_DISCONNECTED_MESSAGE,
    };
  }

  if (!response) {
    return null;
  }

  if (response && FAILURE_RESPONSE_STATUSES.includes(response.status)) {
    if (body) {
      return {...body, status};
    }

    return body;
  }

  if (response?.status === 401) {
    // Sentry.captureMessage(
    //   `Error 401: ${response.url}. Headers: ${JSON.stringify(
    //     getAuthAndBaseHeaders(),
    //   )}. Request init: ${stringifySentryRequestInit(options)}`,
    // );
  }

  if (response?.status === 404) {
    // Sentry.captureMessage(
    //   `Error 404: ${response.url}. Request init: ${stringifySentryRequestInit(options)}`,
    // );

    return {
      message: `404 Not found. Please contact support.`,
      status,
      body,
    };
  }

  if (response?.status === 405) {
    // Sentry.captureMessage(
    //   `Error 405: ${response.url}. Request init: ${stringifySentryRequestInit(options)}`,
    // );

    return {
      message: `405 Method not allowed. Please contact support.`,
      status,
      body,
    };
  }

  if (response?.status === 500) {
    // Sentry.captureMessage(
    //   `Error 500: ${response.url}. Request init: ${stringifySentryRequestInit(options)}`,
    // );

    return {
      message: '500 Internal Server error. Please contact support.',
      status,
      body,
    };
  }

  if (response?.status === 502) {
    // Sentry.captureMessage(
    //   `Error 502: ${response.url}. Request init: ${stringifySentryRequestInit(options)}`,
    // );

    return {
      message: '502 Bad Gateway. Please contact support.',
      status,
      body,
    };
  }

  if (response?.status === 503) {
    // Sentry.captureMessage(
    //   `Error 503: ${response.url}. Request init: ${stringifySentryRequestInit(options)}`,
    // );

    return {
      message: '503 Service Unavailable. Please contact support.',
      status,
      body,
    };
  }

  if (response?.status === 504) {
    // Sentry.captureMessage(
    //   `Error 504: ${response.url}. Request init: ${stringifySentryRequestInit(options)}`,
    // );

    return {
      message: '504 Gateway Timeout. Please contact support.',
      status,
      body,
    };
  }

  return null;
}

export {
  getURL,
  getAndHandleResponseError,
  getAuthAndBaseHeaders,
  getAnonymousHeaders,
  getTokenFromLocalStorage,
  persistTokenToLocalStorage,
  removeTokenFromLocalStorage,
  getChekinURLToken,
};
